/**
 * The KPI strip's icon slots — the six hero marks the shared `stats-shared.html :: kpiStrip`
 * fragment leaves empty for the client to draw (kpi-strip-show-dont-tell).
 *
 * A leaf of its own since stats-panel-unification gave the expanded leaderboard row the same strip:
 * the mapping used to live in `games-list-enhance.js`, which only the `/games` surface runs, and
 * `stats-panel-enhance.js` — the module BOTH surfaces already call on their mounted root — is the
 * one that now needs it. Absorbing it there instead would have grown a module already at 402 logic
 * lines (yellow; red at 450; the one call this move added puts it at 404), so the six entries moved
 * out to a leaf both can import, and `games-list-enhance.js` dropped to 190 in the bargain.
 *
 * The mapping (design § 3.3's starter set, extended to all six by kpi-strip-show-dont-tell): avg
 * net → balance scale (the leaderboard's average convention), declarer/defender win-rate → the role
 * family, ramsch rate → fallen chalice, rounds → the merchant's counting frame (createCountingFrame
 * — already taught for exactly this concept by the stats-v2 run panel), avg bid → the merchant's
 * weight. This deliberately reverses the original "a count needs no emblem" call for rounds/avg bid
 * (Gap 2): the stats-v2 panel shipped counting-frame for a raw rounds count after that call was
 * made, so the KPI strip's own rounds card follows suit.
 *
 * Server-rendered, progressively enhanced: each slot carries its own `data-kpi` token and starts
 * empty, and the card's accessible name is the server-rendered value plus its aria-label, so a run
 * that never happens costs scent, not content.
 */
import { createFallenChalice } from "./icons/game-type-icons.js";
import { createCountingFrame } from "./icons/prompt-icons.js";
import {
  createGatehouse,
  createSeparatedStandardPair,
} from "./icons/role-icons.js";
import { createBalanceScale, createBidWeight } from "./icons/stat-icons.js";

// why: matches .inspector-body .ri-fact-icon's 56px data-card size — the strip went icon-primary,
// adopting the round inspector's own hero-card grammar. The compact variant the leaderboard row
// wears scales the drawn svg down in CSS instead of drawing at a second size
// (`.stats-kpi-compact .ri-fact-icon svg`, skat.css § Games Page —
// the strip's rules live beside `.stats-kpi-hero`, not in § Stats Panel): one factory call, two looks.
export const KPI_ICON_SIZE = 56;

const KPI_TO_ICON = {
  rounds: createCountingFrame,
  avgScore: createBalanceScale,
  declarerWinrate: createGatehouse,
  defenderWinrate: createSeparatedStandardPair,
  ramschRate: createFallenChalice,
  avgBid: createBidWeight,
};

/** Draws every `.ri-fact-icon[data-kpi]` slot under `root`. Idempotent — an already-filled slot is
 *  skipped, so a re-enhance after a fragment swap never doubles a mark; an unmapped token is left
 *  un-drawn rather than throwing (the server's own `Kpi#iconToken` is what fails loud on one). */
export function fillKpiIcons(root) {
  for (const slot of root.querySelectorAll(".ri-fact-icon[data-kpi]")) {
    if (slot.firstChild != null) continue;
    const factory = KPI_TO_ICON[slot.dataset.kpi];
    if (factory != null) slot.appendChild(factory(KPI_ICON_SIZE));
  }
}
